
import { Component, Output, EventEmitter, OnInit, HostListener, ElementRef } from '@angular/core'
import { CommonModule } from '@angular/common';
import { GraphComponent } from './graph/graph.component';
import { TreeComponent } from './tree/tree.component';
import { GraphRelation } from '../../interfaces/visualization.interface';
import { mapRelationsToCytoscapeElements } from './graph.mapper';
import { mapRelationsToTreeElements } from './tree.mapper';
import { ElementDefinition } from 'cytoscape';
import { Subscription } from 'rxjs';
import { VisualizationService } from '../../services/visualization.service';
import { Documents } from '../../interfaces/document.interface';


type ViewMode = 'graph' | 'tree';

@Component({
  selector: 'app-visualization',
  standalone: true,
  imports: [CommonModule, GraphComponent, TreeComponent],
  templateUrl: './visualization.component.html',
  styleUrl: './visualization.component.css'
})
export class VisualizationComponent implements OnInit {
  @Output() backClick = new EventEmitter<void>();

  relations: GraphRelation[] = [];
  isLoading = true;
  error: string | null = null;
  viewMode: ViewMode = 'graph';
// Documents dropdown
  documents: Documents[] = [];
  selectedDocIds: Set<string> = new Set();
  isDropdownOpen = false;
  private subscriptions = new Subscription();
  private _graphElements: ElementDefinition[] = [];
  private _treeElements: ElementDefinition[] = [];

  constructor(
    private visualizationService: VisualizationService,
    private elRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.loadDocuments();
    this.loadKnowledgeGraph();
  }

  get graphElements(): ElementDefinition[] {
    return this._graphElements;
  }

  get treeElements(): ElementDefinition[] {
    return this._treeElements;
  }
 // --- Documents ---

  loadDocuments(): void {
    this.subscriptions.add(
      this.visualizationService.getDocuments().subscribe({
        next: (docs) => {
          this.documents = docs.data;
        },
        error: (err) => console.error('Failed to load documents', err)
      })
    );
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  isSelected(docId: string): boolean {
    return this.selectedDocIds.has(docId);
  }

  toggleDocument(docId: string): void {
    if (this.selectedDocIds.has(docId)) {
      this.selectedDocIds.delete(docId);
    } else {
      this.selectedDocIds.add(docId);
    }
    // Trigger API call on every selection change
    this.onDocumentSelectionChange();
  }

  onDocumentSelectionChange(): void {
    const selectedIds = Array.from(this.selectedDocIds);
    this.subscriptions.add(
      this.visualizationService.getKnowledgeGraph(selectedIds).subscribe({
        next: (response) => {
          if (response.data.hasResult && response.data.result) {
            this.relations = response.data.result;
          } else {
            this.relations = [];
          }
          this._graphElements = mapRelationsToCytoscapeElements(this.relations);
          this._treeElements = mapRelationsToTreeElements(this.relations);
        },
        error: (err) => console.error('Failed to filter by documents', err)
      })
    );
  }

  get dropdownLabel(): string {
    if (this.selectedDocIds.size === 0) return 'All Documents';
    if (this.selectedDocIds.size === 1) {
      // const doc = this.documents.find(d => this.selectedDocIds.has(d.id));
      // return doc?.name ?? '1 selected';
    }
    return `${this.selectedDocIds.size} selected`;
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  loadKnowledgeGraph(): void {
    this.isLoading = true;

    this.subscriptions.add(
      this.visualizationService.getKnowledgeGraph([]).subscribe({
        next: (response) => {
          if (response.data.hasResult && response.data.result) {
            this.relations = response.data.result;
          } else {
            this.relations = [];
          }
          // Compute both once when data loads
          this._graphElements = mapRelationsToCytoscapeElements(this.relations);
          this._treeElements = mapRelationsToTreeElements(this.relations);
          this.isLoading = false;
        },
        error: (error) => {
          this.error = 'Failed to load graph data';
          this.isLoading = false;
          console.error('Error loading graph data:', error);
        }
      })
    );
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
  }

  onBackClick(): void {
    this.backClick.emit();
  }
}
